import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveMap = ({ outageData, onLocationFilter }) => {
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [mapView, setMapView] = useState('heatmap');

  const regions = [
    { id: 'all', name: 'All Regions', count: 2847 },
    { id: 'northeast', name: 'Northeast', count: 892 },
    { id: 'southeast', name: 'Southeast', count: 654 },
    { id: 'midwest', name: 'Midwest', count: 543 },
    { id: 'southwest', name: 'Southwest', count: 421 },
    { id: 'west', name: 'West Coast', count: 337 }
  ];

  const handleRegionSelect = (regionId) => {
    setSelectedRegion(regionId);
    onLocationFilter(regionId);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Map Header */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-foreground">Geographic Distribution</h3>
            <p className="text-sm text-muted-foreground">Outage reports by location</p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={mapView === 'heatmap' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMapView('heatmap')}
            >
              <Icon name="Thermometer" size={16} className="mr-2" />
              Heatmap
            </Button>
            <Button
              variant={mapView === 'markers' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMapView('markers')}
            >
              <Icon name="MapPin" size={16} className="mr-2" />
              Markers
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row">
        {/* Region Filters */}
        <div className="lg:w-1/3 p-4 border-b lg:border-b-0 lg:border-r border-border">
          <h4 className="font-medium text-foreground mb-3">Filter by Region</h4>
          <div className="space-y-2">
            {regions?.map((region) => (
              <button
                key={region?.id}
                onClick={() => handleRegionSelect(region?.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                  selectedRegion === region?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 hover:bg-muted text-foreground'
                }`}
              >
                <span className="font-medium">{region?.name}</span>
                <span className="text-sm opacity-80">{region?.count?.toLocaleString()}</span>
              </button>
            ))}
          </div>

          {/* Legend */}
          <div className="mt-6">
            <h5 className="font-medium text-foreground mb-3">Intensity Legend</h5>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-error rounded"></div>
                <span className="text-sm text-muted-foreground">High (500+ reports)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-warning rounded"></div>
                <span className="text-sm text-muted-foreground">Medium (100-499 reports)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-success rounded"></div>
                <span className="text-sm text-muted-foreground">Low (&lt;100 reports)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Map Container */}
        <div className="lg:w-2/3 h-96 lg:h-[500px] relative">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Service Outage Geographic Distribution"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=39.8283,-98.5795&z=4&output=embed"
            className="border-0"
          />
          
          {/* Overlay Controls */}
          <div className="absolute top-4 right-4 bg-card border border-border rounded-lg p-2 shadow-lg">
            <div className="flex flex-col gap-2">
              <Button variant="ghost" size="sm">
                <Icon name="ZoomIn" size={16} />
              </Button>
              <Button variant="ghost" size="sm">
                <Icon name="ZoomOut" size={16} />
              </Button>
              <Button variant="ghost" size="sm">
                <Icon name="RotateCcw" size={16} />
              </Button>
            </div>
          </div>

          {/* Real-time Update Indicator */}
          <div className="absolute bottom-4 left-4 bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
            <div className="flex items-center gap-2">
              <div className="status-indicator status-online"></div>
              <span className="text-sm font-medium text-foreground">Live Updates</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;